import React, { useEffect, useState } from 'react';
import { AlertTriangle, Clock, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { accountApi, ApiError, type AccountDeletionRequestPublic } from '../../api';
import { getTenantDisplayName } from '../../lib/tenant';
import { useTranslation } from '../../lib/i18n';

function statusTone(status: AccountDeletionRequestPublic['status']) {
  if (status === 'pending') return 'border-amber-200 bg-amber-50/80 text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200';
  if (status === 'rejected') return 'border-rose-200 bg-rose-50/80 text-rose-950 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-200';
  if (status === 'completed') return 'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-300';
  return 'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-300';
}

export default function DeleteAccountSettings() {
  const { t } = useTranslation();
  const orgName = getTenantDisplayName() || '';
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState<AccountDeletionRequestPublic | null>(null);
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await accountApi.getDeletionRequest();
      setRequest(res.request);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load deletion request.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error(t('settingsDelete.reasonTooShort'));
      return;
    }
    if (confirmName.trim() !== orgName.trim()) {
      toast.error(t('settingsDelete.confirmNameMismatch'));
      return;
    }
    if (!window.confirm(t('settingsDelete.confirmPrompt'))) return;

    setSubmitting(true);
    try {
      const res = await accountApi.submitDeletionRequest({
        reason: reason.trim(),
        confirmName: confirmName.trim(),
      });
      setRequest(res.request);
      setReason('');
      setConfirmName('');
      toast.success(t('settingsDelete.submitted'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('settingsDelete.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm(t('settingsDelete.cancelPrompt'))) return;
    setSubmitting(true);
    try {
      const res = await accountApi.cancelDeletionRequest();
      setRequest(res.request);
      toast.success(t('settingsDelete.cancelled'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('settingsDelete.cancelFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const showForm = !request || request.status === 'rejected' || request.status === 'cancelled';

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-rose-200 bg-rose-50/60 p-4 dark:border-rose-900/50 dark:bg-rose-950/30">
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-700 dark:text-rose-400" />
          <div>
            <h3 className="text-sm font-semibold text-rose-950 dark:text-rose-100">{t('settingsDelete.warningTitle')}</h3>
            <p className="mt-1 text-sm leading-relaxed text-rose-900/90 dark:text-rose-200/90">{t('settingsDelete.warningBody')}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('common.loading')}</p>
      ) : request && request.status !== 'cancelled' && request.status !== 'rejected' ? (
        <div className={`rounded-md border p-4 ${statusTone(request.status)}`}>
          <div className="flex items-start gap-2">
            <Clock className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">{t(`settingsDelete.status.${request.status}`)}</p>
              <p className="mt-1 text-xs opacity-90">
                {t('settingsDelete.submittedOn', {
                  date: request.createdAt ? new Date(request.createdAt).toLocaleString() : '—',
                })}
              </p>
              {request.reason ? (
                <p className="mt-2 text-sm">
                  <span className="font-medium">{t('settingsDelete.yourReason')}:</span> {request.reason}
                </p>
              ) : null}
              {request.reviewNote ? (
                <p className="mt-2 text-sm">
                  <span className="font-medium">{t('settingsDelete.adminNote')}:</span> {request.reviewNote}
                </p>
              ) : null}
              {request.status === 'pending' ? (
                <button
                  type="button"
                  onClick={() => void cancel()}
                  disabled={submitting}
                  className="mt-3 text-sm font-semibold underline underline-offset-2"
                >
                  {t('settingsDelete.cancelRequest')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {showForm ? (
        <div className="rounded-md border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-zinc-900">
          <div className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-rose-700 dark:text-rose-400" />
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('settingsDelete.formTitle')}</h3>
          </div>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('settingsDelete.formDesc')}</p>

          <label className="app-label mt-4" htmlFor="delete-reason">
            {t('settingsDelete.reasonLabel')}
          </label>
          <textarea
            id="delete-reason"
            rows={4}
            className="app-field min-h-[6rem] resize-y"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('settingsDelete.reasonPlaceholder')}
          />

          <label className="app-label mt-4" htmlFor="delete-confirm">
            {t('settingsDelete.confirmLabel', { name: orgName || '—' })}
          </label>
          <input
            id="delete-confirm"
            type="text"
            className="app-field"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={orgName}
            autoComplete="off"
          />

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitting || !orgName}
              className="inline-flex items-center gap-2 rounded-md border border-rose-300 bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {submitting ? t('settingsDelete.submitting') : t('settingsDelete.submit')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
